import { AVATAR_SIZES } from "@cmp-src/components/UserAvatar/UserAvatar";
import { UserAvatarStack } from "@cmp-src/components/UserAvatar/UserAvatarStack";
import type {
  DivisionsTree,
  GlobalSearchResourceItem,
  ISchedule,
  IUserFit,
  OfficeWithFloors
} from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import {
  gerCroppedImageUrl,
  getResourceCoverUrl
} from "@gettactic/helpers/src/resources/getResourceCoverUrl";
import { useAuthenticated } from "@gettactic/hooks";
import { Image } from "@v2/components/display/Image/Image";
import { CapacityLabel } from "@v2/components/labels/CapacityLabel/CapacityLabel";
import { classes } from "@v2/components/labels/ResourceLabel/ResourceLabel";
import { useMemo } from "react";
import { GlobalSearchListItemLayout } from "../GlobalSearchListItemLayout";
import { OfficeFloorInfo } from "../OfficeFloorInfo";
import { getOfficeAndFloor } from "../globalSearchOfficeUtils";
import { getUsersFromSchedule } from "../globalSearchScheduleUtils";
import { useResourceScheduleInfo } from "../hooks/useScheduleInfo";
import { FavoriteFilledIcon } from "@v2/icons/FavoriteFilledIcon";

interface ResourceItemProps {
  resource: GlobalSearchResourceItem;
  schedules?: ISchedule[];
  isActive: boolean;
  onSelect: () => void;
  divisionCategories?: {
    offices: Record<string, OfficeWithFloors>;
    tree: DivisionsTree | null;
  };
}

export function ResourceItem({
  resource,
  isActive,
  schedules = [],
  onSelect,
  divisionCategories
}: ResourceItemProps) {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  const { officeWithFloors, officeName, floorName } = getOfficeAndFloor({
    officeId: resource.resource.office_id,
    parentId: resource.resource.parent_id,
    divisionCategories,
    userOfficesById: authenticatedUser?.offices?.byId
  });

  const scheduleTimeInfo = useResourceScheduleInfo(schedules, officeWithFloors);

  // Get schedules for this resource and extract occupying users from slots
  const occupyingUsers = useMemo<IUserFit[]>(() => {
    if (!schedules || schedules.length === 0) return [];

    const userMap = new Map<string, IUserFit>();

    for (const schedule of schedules) {
      const users = getUsersFromSchedule(schedule);
      for (const user of users) {
        userMap.set(user.id, user);
      }
    }

    return Array.from(userMap.values());
  }, [schedules]);

  const iconBg =
    resource.resource.type === "desk"
      ? "bg-blue-100 text-blue-600"
      : "bg-purple-100 text-purple-600";

  const coverImageUrl = getResourceCoverUrl(resource.resource, 74);

  return (
    <GlobalSearchListItemLayout
      isActive={isActive}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          onSelect();
        }
      }}
      className={cn("gap-4 p-3")}
    >
      <div
        className={cn(
          "flex h-10 w-10 items-center justify-center gap-2 rounded-full",
          iconBg
        )}
      >
        <div
          className={cn(
            "h-[50px] w-[50px] flex-shrink-0 overflow-hidden rounded-[5px] bg-neutral-50 md:h-[50px] md:w-[50px]"
          )}
        >
          <Image
            src={
              coverImageUrl ||
              gerCroppedImageUrl(
                "https://cdn.gettactic.com/defaults/room.png",
                50
              )
            }
            className={"h-[50px] rounded-[5px] object-cover md:h-[50px]"}
          />
        </div>
      </div>

      <div className="flex flex-1 flex-col items-start gap-2 overflow-hidden">
        <div className="flex items-center gap-2">
          <h4 className="line-clamp-1 flex items-center gap-1 font-semibold text-gray-900 text-sm">
            {resource.resource.favorite_id ? (
                <FavoriteFilledIcon className={"size-3.5 text-icon-paleGray/20"} />
              ) : (
                null
              )}
            {resource.resource.name}
          </h4>

          {scheduleTimeInfo && (
            <div className="flex items-center gap-2 text-gray-400 text-xs">
              {scheduleTimeInfo.type === "current" ? (
                <>
                  {scheduleTimeInfo.users &&
                  scheduleTimeInfo.users.length > 0 ? (
                    <div className="opacity-30 transition-all transition-duration-1000 hover:opacity-100">
                      <UserAvatarStack
                        avatarSize={AVATAR_SIZES.ROUNDED_6_6}
                        users={scheduleTimeInfo.users}
                        hoverDirection="left"
                        maxVisibleAvatars={3}
                        showRest={false}
                      />
                    </div>
                  ) : scheduleTimeInfo.isMeetingRoom &&
                    scheduleTimeInfo.meetingTitle ? (
                    <span className="max-w-md truncate">
                      Meeting "{scheduleTimeInfo.meetingTitle}"
                    </span>
                  ) : (
                    <span>occupied</span>
                  )}
                  <span>until {scheduleTimeInfo.endTime}</span>
                  {scheduleTimeInfo.freeUntilText && (
                    <span>· {scheduleTimeInfo.freeUntilText}</span>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <span>{scheduleTimeInfo.text}</span>
                  <div className="opacity-30 transition-all transition-duration-1000 hover:opacity-100">
                    {occupyingUsers.length > 0 && (
                      <UserAvatarStack
                        avatarSize={AVATAR_SIZES.ROUNDED_6_6}
                        users={occupyingUsers}
                        hoverDirection="left"
                        maxVisibleAvatars={3}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex w-full min-w-0 items-center justify-between gap-1 truncate md:max-w-auto">
          <div className={classes.ResourceLocation}>
            <OfficeFloorInfo officeName={officeName} floorName={floorName} />
            <CapacityLabel resource={resource.resource} />
          </div>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <div
          className={cn(
            "flex items-center rounded-sm px-2 py-0.5 font-medium text-xs",
            resource.non_available
              ? "border border-gray-300 bg-gray-100 text-gray-700"
              : "border border-gray-200 bg-transparent text-gray-600"
          )}
        >
          <span
            className={cn(
              "mr-1 h-1 w-1 shrink-0 rounded-full",
              resource.non_available ? "hidden" : "bg-green-600"
            )}
          />
          {resource.non_available ? "Not Available" : "Available"}
        </div>
      </div>
    </GlobalSearchListItemLayout>
  );
}
